import React, { useCallback, useState } from "react";
import toast from "react-hot-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ProfileNav from "../../components/ProfileNav";
import Pagination from "../../components/Pagination";
import {
  approveRefundRequests,
  fetchRefundRequests,
  fetchManualPendingRefunds,
  fetchPendingRefunds,
  fetchInProgressRefunds,
  fetchApprovedRefunds,
  fetchDeclinedRefunds,
  escalateRefundManual,
  checkRefundStatusQr,
  resolveRefundRequest,
} from "../../services/organisation";
import { useAuth } from "../../context/AuthContextProvider";

const RefundRequests = () => {
  const { user } = useAuth();
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState("approve"); // 'approve' | 'decline' | 'manual' | 'resolve'
  const [declineReason, setDeclineReason] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [total, setTotal] = useState(0);

  const [resolveAction, setResolveAction] = useState("approve"); // 'approve' | 'decline'
  const [resolvedBy, setResolvedBy] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Updated to a more reasonable default for the new UI
  const [activeTab, setActiveTab] = useState("all"); // 'all' | 'manual-pending' | 'pending' | 'in-progress' | 'approved' | 'declined'
  const queryClient = useQueryClient();

  const getInactiveRequests = useCallback(async () => {
    try {
      let res;
      if (activeTab === "all") {
        res = await fetchRefundRequests(currentPage, itemsPerPage);
        setTotal(res.data?.pagination?.totalCount || 0);
      } else if (activeTab === "manual-pending") {
        res = await fetchManualPendingRefunds();
        const dataList = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
        setTotal(dataList.length);
      } else if (activeTab === "pending") {
        res = await fetchPendingRefunds();
        const dataList = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
        setTotal(dataList.length);
      } else if (activeTab === "in-progress") {
        res = await fetchInProgressRefunds();
        const dataList = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
        setTotal(dataList.length);
      } else if (activeTab === "approved") {
        res = await fetchApprovedRefunds();
        const dataList = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
        setTotal(dataList.length);
      } else if (activeTab === "declined") {
        res = await fetchDeclinedRefunds();
        const dataList = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
        setTotal(dataList.length);
      }
      // Removed toast.success(res.message) to avoid spamming on every fetch
      return res;
    } catch (error) {
      if (error.response) {
        toast.error(error?.response?.data?.message);
      }
    }
  }, [currentPage, itemsPerPage, activeTab]);

  const query = useQuery({
    queryKey: ["getAllRequests", activeTab, currentPage],
    queryFn: getInactiveRequests,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000,
  });

  const openModal = (request, type) => {
    setSelectedRequest(request);
    setActionType(type);
    setDeclineReason(""); // Reset reason
    if (type === "resolve") {
      setResolveAction("approve");
      const defaultResolvedBy = user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : "";
      setResolvedBy(defaultResolvedBy);
    }
    setIsModalOpen(true);
  };

  const handleProcessApproval = async () => {
    if (!selectedRequest) return;

    if (actionType === "decline" && declineReason.trim().length < 10) {
      toast.error("Please provide a reason for declining (at least 10 characters).");
      return;
    }

    if (actionType === "resolve" && resolveAction === "decline" && declineReason.trim().length < 10) {
      toast.error("Please provide a reason for declining (at least 10 characters).");
      return;
    }

    if (actionType === "resolve" && !resolvedBy.trim()) {
      toast.error("Please specify who resolved this refund.");
      return;
    }

    const toastId = toast.loading(`Processing ${actionType === "manual" ? "manual escalation" : actionType === "resolve" ? "resolution" : actionType}...`);

    try {
      let res;
      if (actionType === "manual") {
        res = await escalateRefundManual(selectedRequest.refundId);
      } else if (actionType === "resolve") {
        res = await resolveRefundRequest(selectedRequest.refundId, {
          action: resolveAction,
          resolvedBy: resolvedBy.trim(),
          declineReason: resolveAction === "decline" ? declineReason.trim() : "string"
        });
      } else {
        res = await approveRefundRequests({
          id: selectedRequest.refundId,
          data: actionType === 'approve' ? {
            "action": actionType, // 'approve' or 'decline'
          } : {
            "action": actionType, // 'approve' or 'decline'
            "declineReason": actionType === "decline" ? declineReason : "string"
          }
        });
      }
      toast.success(res.message || `Refund ${actionType === "manual" ? "escalated manually" : actionType === "resolve" ? "resolved" : actionType + "d"} successfully`, { id: toastId });
      queryClient.invalidateQueries({ queryKey: ["getAllRequests", activeTab, currentPage] });
    } catch (error) {
      const msg = error.response?.data?.message || `Failed to ${actionType === "manual" ? "escalate refund manually" : actionType === "resolve" ? "resolve refund" : actionType + " refund"}`;
      toast.error(msg, { id: toastId });
    } finally {
      setIsModalOpen(false);
      setSelectedRequest(null);
      setDeclineReason("");
    }
  };

  const handleCheckStatus = async (request) => {
    const toastId = toast.loading("Checking refund status...");
    try {
      const res = await checkRefundStatusQr(request.refundId);
      toast.success(res.message || "Status checked successfully", { id: toastId });
      queryClient.invalidateQueries({ queryKey: ["getAllRequests", activeTab, currentPage] });
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to check refund status";
      toast.error(msg, { id: toastId });
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRequest(null);
    setDeclineReason("");
  };

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Helper to slice data client-side for non-paginated tabs
  const getDisplayData = () => {
    if (!query?.data) return [];
    const rawData = Array.isArray(query.data.data)
      ? query.data.data
      : (Array.isArray(query.data.data?.data) ? query.data.data.data : []);

    if (activeTab !== "all") {
      const startIndex = (currentPage - 1) * itemsPerPage;
      return rawData.slice(startIndex, startIndex + itemsPerPage);
    }
    return rawData;
  };

  return (
    <div className="h-screen bg-[#f8fafc] font-sans text-[#334155]">
      <article className="h-full flex flex-col gap-4 p-5 max-w-[1200px] mx-auto animate-fade-in">
        {/* Header Section */}
        <section className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold text-[#334155]">Refund Management</h2>
          <div>
            <ProfileNav />
          </div>
        </section>

        {/* Tabs Bar */}
        <div className="flex border-b border-[#e2e8f0] mb-2 gap-2 overflow-x-auto">
          {[
            { id: "all", label: "All Requests" },
            //{ id: "manual-pending", label: "Manual Pending" },
            { id: "pending", label: "Pending" },
            { id: "in-progress", label: "In Progress" },
            { id: "approved", label: "Approved" },
            { id: "declined", label: "Declined" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setCurrentPage(1);
              }}
              className={`px-4 py-2.5 text-sm font-semibold transition-all border-b-2 whitespace-nowrap ${activeTab === tab.id
                  ? "border-[#f58220] text-[#f58220]"
                  : "border-transparent text-[#64748b] hover:text-[#334155] hover:border-[#cbd5e1]"
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Main Card */}
        <div className="bg-white border border-[#e2e8f0] rounded-lg shadow-sm overflow-hidden flex-grow flex flex-col">
          <div className="overflow-x-auto w-full flex-grow">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr>
                  <th className="bg-[#f1f5f9] p-4 text-xs uppercase font-semibold text-[#64748b] border-b border-[#e2e8f0]">Ref ID</th>
                  <th className="bg-[#f1f5f9] p-4 text-xs uppercase font-semibold text-[#64748b] border-b border-[#e2e8f0]">Name</th>
                  <th className="bg-[#f1f5f9] p-4 text-xs uppercase font-semibold text-[#64748b] border-b border-[#e2e8f0]">Customer Email</th>
                  <th className="bg-[#f1f5f9] p-4 text-xs uppercase font-semibold text-[#64748b] border-b border-[#e2e8f0]">Domain</th>
                  <th className="bg-[#f1f5f9] p-4 text-xs uppercase font-semibold text-[#64748b] border-b border-[#e2e8f0]">Transaction Scheme</th>
                  <th className="bg-[#f1f5f9] p-4 text-xs uppercase font-semibold text-[#64748b] border-b border-[#e2e8f0]">Date Created</th>
                  <th className="bg-[#f1f5f9] p-4 text-xs uppercase font-semibold text-[#64748b] border-b border-[#e2e8f0]">Status</th>
                  <th className="bg-[#f1f5f9] p-4 text-xs uppercase font-semibold text-[#64748b] border-b border-[#e2e8f0]">Action</th>
                </tr>
              </thead>
              <tbody>
                {getDisplayData().map((request) => (
                  <tr key={request.refundId} className="border-b border-[#e2e8f0] hover:bg-slate-50 transition-colors">
                    <td className="p-4 text-sm font-medium text-[#334155]">REF-{request.refundId}</td>
                    <td className="p-4 text-sm text-[#334155]">{request.customerName}</td>
                    <td className="p-4 text-sm text-[#334155]">{request.customerEmail}</td>
                    <td className="p-4 text-sm text-[#334155]">{request.merchantName}</td>
                    <td className="p-4 text-sm text-[#334155]">{request.transactionScheme || "---"}</td>
                    <td className="p-4 text-sm text-[#334155]">
                      {new Date(request.requestDate).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </td>
                    <td className="p-4 text-sm">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${request.refundStatus?.toLowerCase() === 'approved' ? 'bg-[#dcfce7] text-[#166534]' :
                          request.refundStatus?.toLowerCase() === 'declined' ? 'bg-[#fee2e2] text-[#991b1b]' :
                            'bg-[#fef3c7] text-[#92400e]'
                        }`}>
                        {request.refundStatus || 'Pending'}
                      </span>
                    </td>
                    {request.refundStatus?.toLowerCase() === 'declined' || request.refundStatus?.toLowerCase() === 'approved' ? (
                      <td className="p-4 text-sm text-[#94a3b8]">---</td>
                    ) : activeTab === 'in-progress' ? (
                      <td className="p-4 text-sm flex gap-2">
                        {request.transactionScheme?.toLowerCase() === 'paytentqr' && (
                          <button
                            onClick={() => handleCheckStatus(request)}
                            className="bg-[#3b82f6] hover:opacity-90 text-white px-4 py-2 rounded text-xs font-semibold transition-opacity"
                          >
                            Check Status
                          </button>
                        )}
                        <button
                          onClick={() => openModal(request, 'resolve')}
                          className="bg-[#6366f1] hover:opacity-90 text-white px-4 py-2 rounded text-xs font-semibold transition-opacity"
                        >
                          Change Status
                        </button>
                      </td>
                    ) : activeTab === 'approved' || activeTab === 'declined' ? (
                      <td className="p-4 text-sm text-[#94a3b8]">---</td>
                    ) : (
                      <td className="p-4 text-sm flex gap-2">
                        <button
                          onClick={() => openModal(request, 'approve')}
                          className="bg-[#22c55e] hover:opacity-90 text-white px-4 py-2 rounded text-xs font-semibold transition-opacity"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => openModal(request, 'decline')}
                          className="bg-[#cbd5e1] hover:bg-[#94a3b8] text-[#334155] px-4 py-2 rounded text-xs font-semibold transition-colors"
                        >
                          Decline
                        </button>
                        {activeTab === 'all' && (
                          <button
                            onClick={() => openModal(request, 'manual')}
                            className="bg-[#f58220] hover:opacity-90 text-white px-4 py-2 rounded text-xs font-semibold transition-opacity"
                          >
                            Manual
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}

                {getDisplayData().length === 0 && (
                  <tr>
                    <td colSpan="8" className="p-8 text-center text-gray-500">
                      No refund requests found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="p-4 border-t border-[#e2e8f0] flex justify-end">
            <Pagination
              totalItems={total}
              itemsPerPage={itemsPerPage}
              paginate={paginate}
              currentPage={currentPage}
            />
          </div>
        </div>
      </article>

      {/* Confirmation/Decline Modal Overlay */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]">
          <div className="bg-white p-6 rounded-xl w-[400px] text-center shadow-xl animate-fade-in-up">
            <h3 className="text-xl font-bold mt-0 mb-2 text-[#334155]">
              {actionType === "approve" ? "Confirm Refund?" : actionType === "manual" ? "Manually Escalate?" : actionType === "resolve" ? "Resolve Refund Request" : "Decline Refund?"}
            </h3>

            {actionType === "resolve" ? (
              <div className="mb-6 text-left flex flex-col gap-4">
                <div>
                  <label className="block text-[#64748b] text-xs font-semibold uppercase mb-1">Action</label>
                  <select
                    className="w-full border border-[#e2e8f0] rounded p-2 text-sm focus:outline-none focus:border-[#f58220] bg-white"
                    value={resolveAction}
                    onChange={(e) => setResolveAction(e.target.value)}
                  >
                    <option value="approve">Approve</option>
                    <option value="decline">Decline</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[#64748b] text-xs font-semibold uppercase mb-1">Resolved By</label>
                  <input
                    type="text"
                    className="w-full border border-[#e2e8f0] rounded p-2 text-sm focus:outline-none focus:border-[#f58220]"
                    placeholder="Enter resolver name..."
                    value={resolvedBy}
                    onChange={(e) => setResolvedBy(e.target.value)}
                  />
                </div>
                {resolveAction === "decline" && (
                  <div>
                    <label className="block text-[#64748b] text-xs font-semibold uppercase mb-1">Decline Reason</label>
                    <textarea
                      className="w-full border border-[#e2e8f0] rounded p-2 text-sm focus:outline-none focus:border-[#f58220]"
                      rows="3"
                      placeholder="Specify decline reason (min 10 characters)..."
                      value={declineReason}
                      onChange={(e) => setDeclineReason(e.target.value)}
                    />
                    <p className="text-[10px] text-[#94a3b8] mt-1"> Minimum 10 characters required. </p>
                  </div>
                )}
              </div>
            ) : actionType === "approve" ? (
              <p className="text-[#64748b] text-sm mb-6">
                You are about to authorize a refund for <strong>{selectedRequest?.customerName || selectedRequest?.name}</strong>. This action is irreversible.
              </p>
            ) : actionType === "manual" ? (
              <p className="text-[#64748b] text-sm mb-6">
                You are about to manually escalate the refund request for <strong>{selectedRequest?.customerName || selectedRequest?.name}</strong>.
              </p>
            ) : (
              <div className="mb-6 text-left">
                <p className="text-[#64748b] text-sm mb-2">
                  Please provide a reason for declining this refund request:
                </p>
                <textarea
                  className="w-full border border-[#e2e8f0] rounded p-2 text-sm focus:outline-none focus:border-[#f58220]"
                  rows="3"
                  placeholder="e.g. Invalid transaction proof (min 10 characters)..."
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                />
                <p className="text-[10px] text-[#94a3b8] mt-1"> Minimum 10 characters required. </p>
              </div>
            )}

            <div className="flex justify-center gap-3">
              <button
                onClick={closeModal}
                className="bg-[#cbd5e1] text-[#334155] px-4 py-2 rounded text-sm font-semibold hover:opacity-90 transition-opacity"
              >
                Cancel
              </button>
              <button
                onClick={handleProcessApproval}
                className={`${actionType === 'approve' ? 'bg-[#f58220]' : actionType === 'manual' ? 'bg-[#3b82f6]' : actionType === 'resolve' ? 'bg-[#6366f1]' : 'bg-[#ef4444]'} text-white px-4 py-2 rounded text-sm font-semibold hover:opacity-90 transition-opacity`}
              >
                {actionType === "approve" ? "Confirm" : actionType === "manual" ? "Escalate" : actionType === "resolve" ? "Resolve" : "Decline"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RefundRequests;
